"use client";

import { m, useInView } from "framer-motion";
import { useRef } from "react";
import SectionHeader from "../sectionHeader";

interface ExpectColumn {
  title: string;
  items: string[];
  variant: "positive" | "negative";
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    columns: ExpectColumn[];
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

function ExpectIcon({ variant }: { variant: ExpectColumn["variant"] }) {
  const src = variant === "positive" ? "/images/icon-checkmark.svg" : "/images/icon-cross.png";

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      width={36}
      height={36}
      aria-hidden="true"
      className="h-9 w-9 shrink-0 object-contain"
    />
  );
}

export default function ExpectSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.05, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col gap-12">
        <SectionHeader title={data?.title} headline={data?.headline} />

        <div className="grid md:grid-cols-2 gap-4">
          {data?.columns?.map((column, index) => (
            <m.div
              key={index}
              variants={itemVariants}
              className={`p-6 lg:p-8 flex flex-col gap-6 ${
                column.variant === "negative" ? "bg-primary" : "bg-surface"
              }`}
            >
              <h3 className="h4 uppercase">{column.title}</h3>
              <ul className="flex flex-col gap-4">
                {column.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ExpectIcon variant={column.variant} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </m.div>
          ))}
        </div>
      </div>
    </m.section>
  );
}
